import { Controller } from "@hotwired/stimulus";
import Chart from "chart.js/auto";

export default class extends Controller {
  static values = {
    payload: String,
    type: { type: String, default: "line" },
  };

  connect() {
    this.renderChart();
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  }

  renderChart() {
    const data = JSON.parse(this.payloadValue);
    if (!data || !data.labels || data.labels.length === 0) {
      this.showEmpty();
      return;
    }

    const type = this.typeValue;
    const config = this.buildConfig(type, data);

    this.chart = new Chart(this.element, config);
  }

  buildConfig(type, data) {
    if (type === "doughnut") {
      return this.doughnutConfig(data);
    } else if (type === "bar") {
      return this.barConfig(data);
    } else {
      return this.lineConfig(data);
    }
  }

  lineConfig(data) {
    const datasets = data.datasets
      ? data.datasets.map((ds) => ({
          label: ds.label,
          data: ds.values,
          borderColor: ds.color || "#6d28d9",
          backgroundColor: ds.fill
            ? this.withAlpha(ds.color || "#6d28d9", 0.1)
            : "transparent",
          borderDash: ds.dashed ? [5, 5] : [],
          fill: !!ds.fill,
          tension: 0.3,
          pointRadius: 2,
        }))
      : [
          {
            data: data.values,
            borderColor: "#6d28d9",
            backgroundColor: "rgba(109, 40, 217, 0.1)",
            fill: true,
            tension: 0.3,
            pointRadius: 2,
          },
        ];

    return {
      type: "line",
      data: { labels: data.labels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: datasets.length > 1 },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                data.currency
                  ? `${ctx.dataset.label || ""}: EUR ${(ctx.raw / 100).toFixed(2)}`
                  : `${ctx.dataset.label || ""}: ${ctx.raw.toLocaleString("de-DE")}`,
            },
          },
        },
        scales: {
          x: { grid: { display: false } },
          y: {
            beginAtZero: true,
            ticks: {
              callback: (value) =>
                data.currency
                  ? `EUR ${(value / 100).toLocaleString("de-DE")}`
                  : value.toLocaleString("de-DE"),
            },
          },
        },
      },
    };
  }

  barConfig(data) {
    return {
      type: "bar",
      data: {
        labels: data.labels,
        datasets: [
          {
            data: data.values,
            backgroundColor: "#6d28d9",
            borderRadius: 4,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
          x: { grid: { display: false } },
          y: { beginAtZero: true },
        },
      },
    };
  }

  doughnutConfig(data) {
    const colors = [
      "#6d28d9",
      "#06b6d4",
      "#f59e0b",
      "#10b981",
      "#ef4444",
      "#8b5cf6",
      "#ec4899",
    ];

    return {
      type: "doughnut",
      data: {
        labels: data.labels,
        datasets: [
          {
            data: data.values,
            backgroundColor: colors.slice(0, data.labels.length),
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "bottom" },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                const pct = ((ctx.raw / total) * 100).toFixed(1);
                return data.currency
                  ? `${ctx.label}: EUR ${(ctx.raw / 100).toFixed(2)} (${pct}%)`
                  : `${ctx.label}: ${ctx.raw.toLocaleString("de-DE")} (${pct}%)`;
              },
            },
          },
        },
      },
    };
  }

  showEmpty() {
    const container = this.element.parentElement;
    this.element.style.display = "none";
    const msg = document.createElement("div");
    msg.className =
      "h-48 border-2 border-dashed border-base-300 rounded-lg text-center content-center";
    msg.innerHTML =
      '<p class="text-base-content/50">No data available for this period.</p>';
    container.appendChild(msg);
  }

  withAlpha(hex, alpha) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
}
